import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { getSharedHeaders } from "./headers";

import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";

declare global {
  interface Window {
    AwsWafIntegration?: {
      fetch: typeof fetch;
    };
  }
}

if (import.meta.env.DEV) {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

export const __FE_INTERNAL_HEADER_AWS_WAF = "x-waf-fetch-mode";

const uri = import.meta.env.VITE_GRAPHQL_URL;

// HTTP connection to the API
const httpLink = createHttpLink({
  // You should use an absolute URL here
  uri: (operation) => {
    const opName = operation.operationName || "";
    return `${uri}?opName=${opName}`;
  },
  fetch: (uri, _options) => {
    const { headers, isWafEnabled } = getHeadersForRequest(_options?.headers);
    const options = { ..._options, headers };

    if (
      isWafEnabled &&
      "AwsWafIntegration" in window &&
      window.AwsWafIntegration
    ) {
      const wafIntegration = window.AwsWafIntegration;

      return wafIntegration.fetch(uri, options);
    }
    return fetch(uri, options);
  },
  fetchOptions: {},
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...getSharedHeaders(),
      ...headers,
    },
  };
});

// Cache implementation
const cache = new InMemoryCache();

// Create the Apollo Client
export const apolloClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "no-cache",
    },
  },
});

function getHeadersForRequest(headers: HeadersInit | undefined): {
  headers: HeadersInit;
  isWafEnabled: boolean;
} {
  if (headers instanceof Headers && headers.has(__FE_INTERNAL_HEADER_AWS_WAF)) {
    headers.delete(__FE_INTERNAL_HEADER_AWS_WAF);
    return {
      headers,
      isWafEnabled: true,
    };
  } else if (
    headers &&
    __FE_INTERNAL_HEADER_AWS_WAF in headers &&
    headers[__FE_INTERNAL_HEADER_AWS_WAF] === "true"
  ) {
    const newHeaders = { ...headers };
    delete newHeaders[__FE_INTERNAL_HEADER_AWS_WAF];
    return {
      headers: newHeaders,
      isWafEnabled: true,
    };
  }

  return {
    headers: headers || {},
    isWafEnabled: false,
  };
}
